"use client";

import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Row, Column, Flex } from "@faire/web/slate/Layout";
import { Typography } from "@faire/web/slate/Typography";
import { legacyComponentCls } from "@faire/web/slate/utils/tracking";
import merge from "lodash/merge";
import React, { useState } from "react";
import styled, { css } from "styled-components";

import { ColorOrKey } from "../../slate/Color";
import { Chevron } from "../../slate/icons/foundation/Chevron";
import { Close } from "../../slate/icons/foundation/Close";
import { Information } from "../../slate/icons/foundation/Information";
import { Spacer } from "../../slate/spacing";
import { DefaultTheme, ITheme, themeOrDefault } from "../../slate/Theme";
import { IActionButtons, ITooltip } from "../../slate/Theme/Tooltip";
import { IconButton } from "../IconButton";

import { useTooltipOffsetToKeepTooltipInsideViewport } from "./__internal__/useTooltipOffsetToKeepTooltipInsideViewport";
import { TOOLTIP_POINTER_SIZE } from "./consts";
import { IDismissableTooltipProps } from "./DismissableTooltip";
import { ITinyTooltipProps, ITooltipProps } from "./Tooltip";

export type Position = "top" | "left" | "right" | "bottom";

export type TooltipColors = {
  borderColor?: ColorOrKey;
  backgroundColor?: ColorOrKey;
  textColor?: ColorOrKey;
  actionButtons?: IActionButtons;
};

export type TooltipVariant = "light" | "dark";

export const TooltipText = styled(Typography).attrs({ color: "inherit" })<{
  $oneLiner?: boolean;
}>`
  ${({ $oneLiner }) =>
    $oneLiner
      ? css`
          white-space: nowrap;
        `
      : ""};
`;

export const TOOLTIP_WRAPPER_Z_INDEX = 9;
export const TooltipWrapper = styled(Column).attrs({
  align: "flex-start",
})<{
  $position: Position;
  $width: number | "auto";
  $offset?: number;
  $colors: TooltipColors;
  $tiny?: boolean;
  $popout?: {
    top?: string;
    left?: string;
    bottom?: string;
    right?: string;
  };
  $tinyRightPadding?: boolean;
}>`
  border: 1px solid;
  border-radius: var(--radius-fs-component-default);
  color: ${({ $colors: colors }) => colors.textColor};
  background-color: ${({ $colors: colors }) => colors.backgroundColor};
  border-color: ${({ $colors: colors }) => colors.borderColor};
  position: absolute;
  ${({ $position: position = "top", $offset: offset = 0 }) => {
    switch (position) {
      case "top":
        return css`
          top: 0;
          left: 50%;
          transform: translate(-50%, -100%) translateX(${-offset}px)
            translateY(-15px);
        `;
      case "bottom":
        return css`
          bottom: 0;
          left: 50%;
          transform: translate(-50%, 100%) translateX(${-offset}px)
            translateY(15px);
        `;
      case "left":
        return css`
          top: 50%;
          left: 0;
          transform: translate(-100%, -50%) translateX(-15px)
            translateY(${-offset}px);
        `;
      case "right":
      default:
        return css`
          top: 50%;
          right: 0;
          transform: translate(100%, -50%) translateX(15px)
            translateY(${-offset}px);
        `;
    }
  }};
  ${({ $popout: popout }) =>
    popout
      ? css`
          ${popout.top
            ? css`
                top: ${popout.top};
              `
            : ""}
          ${popout.bottom
            ? css`
                bottom: ${popout.bottom};
              `
            : ""}
          ${popout.left
            ? css`
                left: ${popout.left};
              `
            : ""}
          ${popout.right
            ? css`
                right: ${popout.right};
              `
            : ""}
        `
      : ""}
  z-index: ${TOOLTIP_WRAPPER_Z_INDEX};
  width: ${({ $width: width }) => (width === "auto" ? width : `${width}px`)};
  ${({ $tiny: tiny, $tinyRightPadding: tinyRightPadding }) => {
    if (tiny) {
      return css`
        padding: 11px;
      `;
    } else {
      return tinyRightPadding
        ? css`
            padding: 23px 11px 0 23px;
          `
        : css`
            padding: 23px 23px 0 23px;
          `;
    }
  }}
  max-width: 80vw;
`;

export const OuterWrapper = styled.span<{
  $dismissible?: boolean;
  $isOpen?: boolean;
  $popout?: boolean;
  $hidden?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  ${({ $popout }) => ($popout ? "" : "position: relative;")}
  ${TooltipWrapper} {
    display: ${({ $isOpen, $hidden }) =>
      $isOpen && !$hidden ? "flex" : "none"};
  }
  ${({ $dismissible, $hidden }) =>
    $dismissible || $hidden
      ? ""
      : css`
          &:hover {
            ${TooltipWrapper} {
              display: flex;
            }
          }
          &:focus:active {
            ${TooltipWrapper} {
              display: flex;
            }
          }
          &:focus-within:active {
            ${TooltipWrapper} {
              display: flex;
            }
          }
        `}
`;

export const TOOLTIP_TIP_Z_INDEX = -100;
export const Tip = styled.div<{
  $position: Position;
  $offset?: number;
}>`
  background: inherit;
  position: absolute;
  z-index: ${TOOLTIP_TIP_Z_INDEX};
  display: block;
  width: ${TOOLTIP_POINTER_SIZE}px;
  height: ${TOOLTIP_POINTER_SIZE}px;
  ${({ $position, $offset = 0 }) => {
    switch ($position) {
      case "top":
        return css`
          left: calc(50% + ${$offset}px);
          bottom: 0;
          transform: translate(-50%, 50%) rotate(45deg);
        `;
      case "bottom":
        return css`
          left: calc(50% + ${$offset}px);
          top: 0;
          transform: translate(-50%, -50%) rotate(225deg);
        `;
      case "left":
        return css`
          top: calc(50% + ${$offset}px);
          right: 0;
          transform: translate(50%, -50%) rotate(315deg);
        `;
      case "right":
      default:
        return css`
          top: calc(50% + ${$offset}px);
          left: 0;
          transform: translate(-50%, -50%) rotate(135deg);
        `;
    }
  }};
  border: inherit;
  border-top-color: transparent;
  border-left-color: transparent;
`;

export const SpaceBetweenIconAndTooltip = styled.div<{ $position: Position }>`
  position: absolute;
  ${({ $position }) => {
    switch ($position) {
      case "top":
        return css`
          left: 0;
          bottom: -16px;
          height: 16px;
          width: 100%;
        `;
      case "bottom":
        return css`
          left: 0;
          top: -16px;
          height: 16px;
          width: 100%;
        `;
      case "left":
        return css`
          top: 0;
          right: -16px;
          height: 100%;
          width: 16px;
        `;
      case "right":
      default:
        return css`
          top: 0;
          left: -16px;
          height: 100%;
          width: 16px;
        `;
    }
  }};
`;

export const ActionButtons: React.FC<{
  theme?: ITheme;
  current: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  colors?: TooltipColors;
  variant?: TooltipVariant;
  autoFocusNext?: boolean;
  "data-test-id"?: string;
}> = ({
  theme = DefaultTheme,
  current,
  total,
  onPrev,
  onNext,
  colors,
  variant = "dark",
  autoFocusNext = false,
  "data-test-id": dataTestId,
}) => {
  colors = mergeTooltipColors(theme, variant, colors);
  const { strictLocalize } = useStrictLocalization();

  return (
    <Row align="center" justify="space-between" style={{ width: "100%" }}>
      <Typography variant="paragraphSansRegular" color={colors?.textColor}>
        {current + 1}/{total}
      </Typography>
      <Row>
        <StyledIconButton
          aria-label={strictLocalize({
            id: "p1TqDF",
            defaultMessage: "Previous tip",
            description: {
              text: "alt text for screen reader for the < icon button",
            },
          })}
          className="override"
          data-test-id={dataTestId ? `${dataTestId}-prev` : undefined}
          disabled={current === 0}
          onClick={onPrev}
          shape="square"
          size="small"
        >
          <Chevron direction="left" />
        </StyledIconButton>
        <Spacer width={4} />
        <StyledIconButton
          aria-label={strictLocalize({
            id: "9R+9Nz",
            defaultMessage: "Next tip",
            description: {
              text: "alt text for screen reader for the > icon button",
            },
          })}
          className="override"
          data-test-id={dataTestId ? `${dataTestId}-next` : undefined}
          disabled={current === total - 1}
          onClick={onNext}
          shape="square"
          size="small"
          autoFocus={autoFocusNext}
        >
          <Chevron direction="right" />
        </StyledIconButton>
      </Row>
    </Row>
  );
};

const StyledIconButton = styled(IconButton)`
  &.override:disabled > div,
  &.override[disabled] > div {
    background: ${Core.surface.default};
  }
`;

const toTooltipColors = (themeTooltip: ITooltip): TooltipColors => ({
  textColor: themeTooltip.color,
  backgroundColor: themeTooltip.background,
  borderColor: themeTooltip.border,
  actionButtons: themeTooltip.actionButtons,
});

export const mergeTooltipColors = (
  theme: ITheme,
  variant: TooltipVariant,
  colors: TooltipColors | undefined
): TooltipColors => {
  const darkVariantDefaults: TooltipColors = {
    textColor: Core.text.inverse,
    backgroundColor: Core.surface.default,
    borderColor: Core.border.default,
  };
  const lightVariantDefaults: TooltipColors = {
    textColor: Core.text.default,
    backgroundColor: Core.surface.inverse,
    borderColor: Core.border.muted,
  };

  const defaultColors =
    variant === "light" ? lightVariantDefaults : darkVariantDefaults;
  const themeColors = toTooltipColors(
    variant === "light" ? theme?.tooltips?.default : theme?.tooltips?.primary
  );
  const borderFallback = { borderColor: colors?.backgroundColor };

  return merge({}, defaultColors, themeColors, borderFallback, colors);
};

export const getContents = (
  currentIndex: number,
  contents?:
    | {
        title?: string;
        bodies?: string[];
      }
    | {
        title?: string;
        body?: string;
      }[]
): {
  currentTitle?: string;
  currentBody?: string;
  numIndices: number;
} => {
  let titles: any;
  if (contents === undefined) {
    titles = undefined;
  } else if (Array.isArray(contents)) {
    titles = contents.map((c) => c.title);
  } else if (contents.bodies === undefined) {
    titles = [contents.title!];
  } else {
    titles = Array(contents.bodies.length).fill(contents.title);
  }

  let bodies: any;
  if (contents === undefined) {
    bodies = undefined;
  } else if (Array.isArray(contents)) {
    bodies = contents.map((c) => c.body);
  } else {
    bodies = contents.bodies;
  }

  const numIndices = Math.max(titles?.length || 0, bodies?.length || 0);

  const currentTitle = titles ? titles[currentIndex] : undefined;

  const currentBody = bodies ? bodies[currentIndex] : undefined;

  return {
    currentTitle,
    currentBody,
    numIndices,
  };
};

const CloseWrapper = styled(IconButton)`
  flex-shrink: 0;
  margin-block-start: -12px; // Close button is bumped up in design so it visually lines up better
`;

const TextContainer = styled(Flex).attrs({ direction: "row-reverse" })`
  width: 100%;
`;

const TextWrapper = styled(Column).attrs({ align: "flex-start" })`
  flex-grow: 1;
`;

export const _buildTooltip = (
  tooltipProps: ITooltipProps & IDismissableTooltipProps,
  dismissible: boolean
) => {
  const {
    children,
    position = "top",
    width = 336,
    pointerOffset = 0,
    className,
    icon,
    iconColor,
    targetNode,
    innerClassName,
    primary,
    variant,
    contents,
    action,
    isOpen,
    hidden = false,
    colors,
    popout,
    "data-test-id": dataTestId,
    innerRef,
    theme: styledComponentsTheme,
    onIndexChange,
    onDismiss,
    keepInsideViewport = false,
  } = tooltipProps;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOpenForDismissible, setIsOpenForDismissible] = useState(true);
  // track the hovered state to recalculate the offset when the tooltip is opened
  const [isHovered, setIsHovered] = useState(false);
  const effectiveIsOpen = dismissible ? isOpenForDismissible : isOpen;

  const theme = themeOrDefault(styledComponentsTheme);
  const tooltipVariant = getTooltipVariant(variant, primary);

  const handleClose = (e?: React.MouseEvent) => {
    setIsOpenForDismissible(false);
    if (onDismiss) {
      onDismiss(e);
    }
  };

  const { currentTitle, currentBody, numIndices } = getContents(
    currentIndex,
    contents
  );

  const mergedColors = mergeTooltipColors(theme, tooltipVariant, colors);

  const { strictLocalize } = useStrictLocalization();

  const { offset, tooltipBoxRef } = useTooltipOffsetToKeepTooltipInsideViewport(
    keepInsideViewport,
    position,
    pointerOffset,
    isHovered
  );

  return (
    <OuterWrapper
      className={className}
      $dismissible={dismissible}
      $isOpen={effectiveIsOpen}
      $popout={!!popout}
      ref={innerRef}
      $hidden={hidden}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
    >
      {targetNode ?? icon ?? <Information fontSize="16px" color={iconColor} />}

      <TooltipWrapper
        $position={position}
        $width={width}
        $offset={offset}
        className={legacyComponentCls(innerClassName)}
        $colors={mergedColors}
        role="tooltip"
        $popout={popout}
        $tinyRightPadding={dismissible || numIndices > 1}
        data-test-id={dataTestId}
        ref={tooltipBoxRef}
      >
        <TextContainer>
          {dismissible ? (
            <>
              <CloseWrapper
                aria-label={strictLocalize({
                  id: "sqyFFG",
                  defaultMessage: "Close",
                  description: {
                    text: "alt text for screen reader for the close icon button",
                  },
                })}
                data-test-id={dataTestId ? `${dataTestId}-close` : undefined}
                onClick={handleClose}
                shape="square"
                size="small"
              >
                <Close />
              </CloseWrapper>
              <Spacer height={4} />
            </>
          ) : null}
          <TextWrapper>
            {currentTitle ? (
              <>
                <TooltipText variant="paragraphSansMedium">
                  {currentTitle}
                </TooltipText>
                <Spacer height={4} />
              </>
            ) : null}
            {currentBody ? (
              <TooltipText variant="paragraphSansRegular">
                {currentBody}
              </TooltipText>
            ) : null}
            {children}
            <Spacer height={16} />
          </TextWrapper>
        </TextContainer>
        {action ? (
          <>
            {action}
            <Spacer height={16} />
          </>
        ) : null}
        {numIndices > 1 ? (
          <>
            <ActionButtons
              theme={theme}
              current={currentIndex}
              total={numIndices}
              onPrev={() => {
                const newIndex = currentIndex - 1;
                setCurrentIndex(newIndex);
                onIndexChange && onIndexChange(newIndex);
              }}
              onNext={() => {
                const newIndex = currentIndex + 1;
                setCurrentIndex(newIndex);
                onIndexChange && onIndexChange(newIndex);
              }}
              colors={colors}
              variant={variant}
            />
            <Spacer height={11} />
          </>
        ) : (
          <Spacer height={7} />
        )}
        <Tip $position={position} $offset={offset} />
        <SpaceBetweenIconAndTooltip $position={position} />
      </TooltipWrapper>
    </OuterWrapper>
  );
};

export const _buildTinyTooltip = (tinyTooltipProps: ITinyTooltipProps) => {
  const {
    children,
    position = "top",
    pointerOffset = 0,
    className,
    icon,
    iconColor,
    targetNode,
    innerClassName,
    primary,
    variant,
    text,
    isOpen,
    colors,
    popout,
    innerRef,
    theme: styledComponentsTheme,
    keepInsideViewport = false,
    hidden = false,
  } = tinyTooltipProps;

  const theme = themeOrDefault(styledComponentsTheme);
  // track the hovered state to recalculate the offset when the tooltip is opened
  const [isHovered, setIsHovered] = useState(false);

  const tooltipVariant = getTooltipVariant(variant, primary);

  const { offset, tooltipBoxRef } = useTooltipOffsetToKeepTooltipInsideViewport(
    keepInsideViewport,
    position,
    pointerOffset,
    isHovered
  );

  return (
    <OuterWrapper
      className={className}
      $isOpen={isOpen}
      $popout={!!popout}
      ref={innerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      $hidden={hidden}
    >
      {targetNode ?? icon ?? <Information fontSize="16px" color={iconColor} />}
      <TooltipWrapper
        $position={position}
        $width="auto"
        $offset={offset}
        $tiny={true}
        className={legacyComponentCls(innerClassName)}
        $colors={mergeTooltipColors(theme, tooltipVariant, colors)}
        $popout={popout}
        ref={tooltipBoxRef}
      >
        {text ? <TooltipText $oneLiner>{text}</TooltipText> : null}
        {children}
        <Tip $position={position} $offset={offset} />
      </TooltipWrapper>
    </OuterWrapper>
  );
};

const getTooltipVariant = (
  variant: TooltipVariant | undefined,
  primary: boolean | undefined
) => {
  if (variant === undefined && primary === undefined) {
    return "dark";
  } else if (variant !== undefined) {
    return variant;
  } else {
    return primary ? "dark" : "light";
  }
};
